import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Heart, MapPin, ExternalLink, Star, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { addLocalBookmark, encodeCardId, getLocalBookmarks } from "@/lib/bookmarks";
import { getSessionId } from "@/lib/session_id";
import { toast } from "sonner";
import BookNowDialog from "@/components/BookNowDialog";
import CardImage from "@/components/CardImage";
import CardDetailDialog from "@/components/CardDetailDialog";
import { handleBookIntent } from "@/lib/bookingPreference";

/**
 * Phase 1 design refresh — grid view for /explore.
 *
 * A read-only sibling of the swipe deck. Shares the exact `/discover`
 * fetch shape ExploreDeckPage builds (so filters/vibes/location/budget
 * apply identically) and reuses `BookNowDialog` for booking. The deck
 * page itself is untouched — this is a pure viewport switch.
 *
 * Pagination: 20-per-page fetch. When the user scrolls near the last
 * row we transparently request the next page and dedupe by card.id.
 *
 * Card design intentionally mirrors the deck's small-card visual
 * language: portrait hero image, name, location, price, star rating,
 * favorite heart, and a gold Book button.
 */

const PAGE_LIMIT = 500;   // grid view — backend hard-caps at 500. Fetch
                          // one big batch so we don't paginate on scroll.

// Mirror BookNowDialog gating from ExploreFiltersPage / ExploreDeckPage.
function isTravelbioCard(card) {
  return card?.provider === "travelbio" || Boolean(card?.metadata?.card_id);
}

function openViaAnchor(url) {
  if (!url) return;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return;
  } catch { return; }
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { try { document.body.removeChild(a); } catch {} }, 100);
}

function GridCard({ card, onBook, onOpenDetail }) {
  const [saved, setSaved] = useState(() => {
    try {
      return getLocalBookmarks().some((b) => b.id === card.id);
    } catch {
      return false;
    }
  });
  // Bug fix — Explore was showing the backend's `placehold.co` beige
  // "Photo coming soon" URL for every TravelBio destination that hasn't
  // been enriched by the async photo pipeline yet, which read as
  // "images not loading". Treat those (plus the older loremflickr
  // shots) as missing so the native cream placeholder tile renders
  // instead, matching the /booked and /chapters placeholder pattern.
  const heroImage = (card.images || []).find((u) => {
    if (!u) return false;
    const lower = u.toLowerCase();
    return !lower.includes("loremflickr.com") && !lower.includes("placehold.co");
  }) || card.featured_image || card.metadata?.photos?.[0] || null;
  const loc = card.location || {};
  const locationStr = [loc.city, loc.state, loc.country, loc.port, loc.destination]
    .filter(Boolean).join(", ");
  const currSymbol = card.currency === "EUR" ? "\u20AC" : "$";
  const rating = card.rating || card.metadata?.rating;

  const save = (e) => {
    e.stopPropagation();
    if (saved) return;
    setSaved(true);
    addLocalBookmark(card);
    api.post(`/destinations/${encodeCardId(card.id)}/swipe`, { direction: "save" })
      .catch(() => {});
    toast.success(`${card.name} saved \u2726`);
  };

  const book = (e) => {
    e.stopPropagation();
    if (isTravelbioCard(card)) {
      onBook(card);
      return;
    }
    if (card.booking_url) {
      const url = card.booking_url.startsWith("http")
        ? card.booking_url
        : `${process.env.REACT_APP_BACKEND_URL}${card.booking_url}`;
      openViaAnchor(url);
      return;
    }
    onBook(card);
  };

  return (
    <article
      data-testid={`grid-card-${card.id}`}
      onClick={() => onOpenDetail?.(card)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenDetail?.(card); }
      }}
      className="group cursor-pointer rounded-2xl overflow-hidden bg-tb-cream border border-tb-cream-3 shadow-[0_10px_28px_-20px_rgba(0,0,0,0.35)] hover:shadow-[0_18px_40px_-20px_rgba(0,0,0,0.45)] hover:-translate-y-0.5 transition-all"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-tb-cream-2">
        <CardImage
          card={{ ...card, featured_image: heroImage, images: [] }}
          className="w-full h-full"
          imgClassName="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          alt={card.name}
          loading="lazy"
          draggable={false}
        />
        {/* Chip badge top-left */}
        {card.chip_label && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur-sm text-white font-hand text-[11px]">
            {card.chip_label}
          </span>
        )}
        {/* Favorite heart top-right — forest green outline / white
            filled bg (user-requested 2026-07). Filled forest state
            when saved. */}
        <button
          data-testid={`grid-card-save-${card.id}`}
          onClick={save}
          aria-label={saved ? "Saved" : "Save"}
          className={
            "absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center tb-tap transition border-2 " +
            (saved
              ? "bg-tb-forest text-white border-tb-forest"
              : "bg-white text-tb-forest border-tb-forest hover:bg-tb-forest-soft")
          }
        >
          <Heart className="w-4.5 h-4.5" strokeWidth={2} fill={saved ? "currentColor" : "none"} />
        </button>
      </div>

      <div className="p-3 sm:p-4">
        <h3 className="font-serif-it text-base md:text-lg text-tb-navy leading-tight line-clamp-2">
          {card.name}
        </h3>
        {locationStr && (
          <p className="mt-1 flex items-center gap-1 font-hand text-xs text-tb-navy/60">
            <MapPin className="w-3 h-3" /> <span className="line-clamp-1">{locationStr}</span>
          </p>
        )}

        <div className="mt-2.5 flex items-end justify-between gap-2">
          <div className="min-w-0 flex-1">
            {rating ? (
              <div className="flex items-center gap-1 mb-0.5">
                <Star className="w-3 h-3 text-tb-gold" fill="currentColor" strokeWidth={0} />
                <span className="font-hand text-xs text-tb-navy/80">{Number(rating).toFixed(1)}</span>
              </div>
            ) : null}
            {card.price_from != null ? (
              // Stack the amount and the "per person / per night / per
              // week / per stay" label so the label never gets clipped
              // by the Book button on narrow grid tiles (2-col mobile).
              <div className="flex flex-col items-start leading-tight">
                <span className="font-serif text-base font-semibold text-tb-navy">
                  {currSymbol}{Math.round(card.price_from).toLocaleString()}
                </span>
                {card.price_label && (
                  <span className="font-hand text-[10px] text-tb-navy/50 truncate max-w-full">
                    {card.price_label}
                  </span>
                )}
              </div>
            ) : (
              <span className="font-hand text-xs text-tb-navy/45 italic">Contact for pricing</span>
            )}
          </div>
          {card.booking_url && !isTravelbioCard(card) ? (
            <a
              data-testid={`grid-card-book-${card.id}`}
              href={card.booking_url.startsWith("http")
                     ? card.booking_url
                     : `${process.env.REACT_APP_BACKEND_URL}${card.booking_url}`}
              target="_blank"
              rel="noopener nofollow noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="shrink-0 inline-flex items-center gap-1 rounded-full bg-tb-navy text-tb-gold-bright border border-tb-gold px-3 h-9 font-hand text-sm hover:brightness-110 tb-tap no-underline"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Book
            </a>
          ) : (
            <button
              data-testid={`grid-card-book-${card.id}`}
              onClick={book}
              className="shrink-0 inline-flex items-center gap-1 rounded-full bg-tb-navy text-tb-gold-bright border border-tb-gold px-3 h-9 font-hand text-sm hover:brightness-110 tb-tap"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Book
            </button>
          )}
        </div>
        {/* Rewards multiplier pill — appears only on cards whose
            resolver-attached `booking_override.rewards_multiplier`
            is > 1.  Silent otherwise. */}
        {(card.booking_override?.rewards_multiplier ?? 1) > 1 && (
          <div
            data-testid={`grid-card-multiplier-${card.id}`}
            className="mt-2 pt-2 border-t border-tb-cream-3 font-hand text-[11px] leading-snug text-tb-navy/85"
          >
            <span className="font-semibold">
              Earn {card.booking_override.rewards_multiplier}X TravelBio Points
            </span>
          </div>
        )}
      </div>
    </article>
  );
}

export default function ExploreGrid({ params, onBook }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [bookingCard, setBookingCard] = useState(null);
  // Card detail modal — opened on tap of any grid card body. Read the
  // initial value from the URL so `/explore?card=<id>` deep-links auto-
  // open. Keeps state in sync with `?card=` via replaceState so back-
  // button behavior stays clean.
  const [detailCard, setDetailCard] = useState(null);
  const initialCardIdRef = useRef(
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("card") || ""
      : "",
  );
  // Parent (ExploreFiltersPage) owns the smart booking router
  // (`handleBookIntent` → same-tab CJ redirect via `bookNow`, with the
  // BookNowDialog fallback for cards missing a CJ URL). If no `onBook`
  // is provided we fall back to opening the multi-stay dialog inline,
  // matching the pre-refactor default.
  const openBookingSmart = onBook || setBookingCard;

  const openDetail = useCallback((card) => {
    if (!card) return;
    setDetailCard(card);
    if (typeof window !== "undefined" && card.id) {
      const u = new URL(window.location.href);
      u.searchParams.set("card", card.id);
      window.history.replaceState({}, "", u.toString());
    }
  }, []);
  const closeDetail = useCallback(() => {
    setDetailCard(null);
    if (typeof window !== "undefined") {
      const u = new URL(window.location.href);
      if (u.searchParams.has("card")) {
        u.searchParams.delete("card");
        window.history.replaceState({}, "", u.toString());
      }
    }
  }, []);
  const seenIdsRef = useRef(new Set());
  const sentinelRef = useRef(null);

  // Normalize `params` into a stable key so effect deps don't churn on
  // every parent re-render.
  const paramsKey = useMemo(() => JSON.stringify(params), [params]);

  // Initial load + reload when filter params change.
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setCards([]);
    setOffset(0);
    seenIdsRef.current = new Set();
    const p = { ...params, limit: PAGE_LIMIT, offset: 0, currency: "USD", session_id: getSessionId() };
    api.get("/discover", { params: p, signal: ctrl.signal })
      .then(({ data }) => {
        const list = data.cards || [];
        list.forEach((c) => c.id && seenIdsRef.current.add(c.id));
        setCards(list);
        setTotal(data.total ?? list.length);
        setHasMore(Boolean(data.has_more));
        setOffset(data.next_offset ?? list.length);
        // Deep-link `/explore?card=<id>` — auto-open the detail modal
        // once the matching card is in the initial fetch. One-shot,
        // clears the ref so filter changes don't keep re-triggering.
        const wantId = initialCardIdRef.current;
        if (wantId) {
          const hit = list.find((c) => c.id === wantId);
          if (hit) setDetailCard(hit);
          initialCardIdRef.current = "";
        }
      })
      .catch(() => {
        setCards([]);
        setHasMore(false);
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  const fetchMore = useCallback(() => {
    if (!hasMore || fetchingMore) return;
    setFetchingMore(true);
    const p = { ...params, limit: PAGE_LIMIT, offset, currency: "USD", session_id: getSessionId() };
    api.get("/discover", { params: p })
      .then(({ data }) => {
        const list = (data.cards || []).filter((c) => !c.id || !seenIdsRef.current.has(c.id));
        list.forEach((c) => c.id && seenIdsRef.current.add(c.id));
        setCards((prev) => [...prev, ...list]);
        setHasMore(Boolean(data.has_more));
        setOffset(data.next_offset ?? (offset + list.length));
      })
      .catch(() => setHasMore(false))
      .finally(() => setFetchingMore(false));
  }, [hasMore, fetchingMore, params, offset]);

  // IntersectionObserver on the sentinel row → auto-load next page.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchMore();
      },
      { rootMargin: "600px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, fetchMore]);

  return (
    <div data-testid="explore-grid" className="w-full">
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="rounded-2xl bg-tb-cream-2 border border-tb-cream-3 aspect-[4/5] animate-pulse"
            />
          ))}
        </div>
      ) : cards.length === 0 ? (
        // Silent empty state — no dashed box, no message. When the
        // featured-destinations feed has nothing, the page just flows
        // through to the next section (e.g. InlineStaySearch below).
        null
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
            {cards.map((card) => (
              <GridCard key={card.id} card={card} onBook={openBookingSmart} onOpenDetail={openDetail} />
            ))}
          </div>

          {hasMore && (
            <div ref={sentinelRef} className="mt-8 flex justify-center py-6">
              {fetchingMore ? (
                <div className="flex items-center gap-2 text-tb-navy/60 font-hand text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading more…
                </div>
              ) : (
                <button
                  onClick={fetchMore}
                  className="rounded-full border-2 border-tb-cream-3 bg-tb-cream px-5 py-2 font-hand text-sm text-tb-navy hover:border-tb-gold tb-tap"
                >
                  Show more
                </button>
              )}
            </div>
          )}

          <p className="mt-6 text-center font-hand text-xs text-tb-navy/45">
            Showing {cards.length}{total ? ` of ${total}` : ""}
          </p>
        </>
      )}

      {bookingCard && (
        <BookNowDialog
          destination={bookingCard}
          onClose={() => setBookingCard(null)}
        />
      )}

      {detailCard && (
        <CardDetailDialog
          card={detailCard}
          onClose={closeDetail}
          onBook={(c, dates) => { openBookingSmart(c, dates); }}
        />
      )}
    </div>
  );
}
